import {
  ApplicationRef,
  DestroyRef,
  Component,
  EnvironmentInjector,
  EventEmitter,
  Injector,
  Output,
  createComponent,
  effect,
  inject,
  makeEnvironmentProviders,
  provideAppInitializer,
} from '@angular/core';
import { AbstractControl, FormArray, FormGroup } from '@angular/forms';
import { CanDeactivateFn, ChildrenOutletContexts, NavigationEnd, Route, Router, Routes } from '@angular/router';
import { firstValueFrom, isObservable } from 'rxjs';
import { ToastService } from '../../../core/services/toaster';

/** Unsaved-changes pop-up (Save / Close / Save & Close), route guard and modal interception in one file; enable with provideUnsavedChangesGuard(). */

/** What the user picked. `stay` = dismissed with the X: nothing happens. */
export type UnsavedChoice = 'save' | 'close' | 'save-close' | 'stay';

/** Optional: lets a screen decide what "changed" means and how a save without navigation works. */
export interface HasUnsavedChanges {
  /** True when a create screen has any entered value, or an edit screen has at least one changed value. */
  hasUnsavedChanges(): boolean;
  /** Saves with the screen's own validations and without navigating; true only when saved. */
  saveChanges(): Promise<boolean>;
}

//
// Flags a screen commonly uses for "view only" / "request in flight" - never prompt in those states.
const READ_ONLY_FLAGS = ['isViewMode', 'isView', 'isHistoryMode', 'readOnly'];
const BUSY_FLAGS = ['saving', 'isSaving', 'submitting', 'isSubmitting'];
// Existing save entry points, tried in this order when a screen has no saveChanges().
const SAVE_METHODS = ['saveChanges', 'onSave', 'save', 'onSubmit', 'submit', 'submitForm', 'saveForm'];

// A modal-style form that is closed is not "unsaved", even though its form object still exists.
const MODAL_FLAGS = ['isModalOpen', 'showModal', 'isFormOpen', 'showForm'];
// A save that just succeeded usually navigates away before the form is marked pristine.
const JUST_SAVED_MS = 3000;

const SAVE_WAIT_MS = 20000;
const POLL_MS = 100;
const BYPASS_GRACE_MS = 800;
// "+ Add New" / "Add New" buttons.
const ADD_NEW = /^\+?\s*add\s+new\b/i;
// Login / password pages: never prompt (login redirects to the dashboard).
const AUTH_PAGE = /\/(login|changepassword|verifyotp|forgetpassword|account-not-found|password-expired)(\/|\?|#|$)/i;
const onAuthPage = (): boolean => AUTH_PAGE.test(window.location.pathname);

@Component({
  selector: 'app-unsaved-changes-dialog',
  standalone: true,
  templateUrl: './unsaved-changes-dialog.html',
  styleUrl: './unsaved-changes-dialog.scss',
})
export class UnsavedChangesDialog {
  @Output() choice = new EventEmitter<UnsavedChoice>();

  pick(choice: UnsavedChoice): void {
    this.choice.emit(choice);
  }

  // ── Opening the pop-up (no template needed in the calling screen) ──────

  private static pending: Promise<UnsavedChoice> | null = null;
  private static lastSuccessAt = 0;
  // While a screen's own save is running, the navigation it triggers afterwards must not be asked about again.
  private static bypass = false;
  // True only after the user really typed / picked something on the current screen. A form can turn `.ng-dirty` on its own
  // (code filling it on load, a dropdown normalising its value, a bug fix that patches the form): that alone must not prompt.
  private static userEdited = false;

  /** A trusted (real user) edit happened; from here dirty forms count as unsaved. */
  static noteUserEdit(): void {
    UnsavedChangesDialog.userEdited = true;
  }

  /** Back to "nothing edited": a new screen, a saved / discarded form, or a closed modal. */
  static resetUserEdit(): void {
    UnsavedChangesDialog.userEdited = false;
  }

  /** Shows the pop-up and resolves with the user's choice. One at a time (a double-clicked Cancel shares it). */
  static open(injector: Injector): Promise<UnsavedChoice> {
    if (UnsavedChangesDialog.pending) return UnsavedChangesDialog.pending;

    const appRef = injector.get(ApplicationRef);
    UnsavedChangesDialog.pending = new Promise<UnsavedChoice>((resolve) => {
      const ref = createComponent(UnsavedChangesDialog, { environmentInjector: injector.get(EnvironmentInjector) });
      appRef.attachView(ref.hostView);
      document.body.appendChild(ref.location.nativeElement);

      const sub = ref.instance.choice.subscribe((choice) => {
        sub.unsubscribe();
        appRef.detachView(ref.hostView);
        ref.destroy();
        UnsavedChangesDialog.pending = null;
        resolve(choice);
      });
    });
    return UnsavedChangesDialog.pending;
  }

  // ── The whole behaviour ─────────────────────────────────────────────────

  /** True = go ahead (leave / add next entry), false = stay; asks Save / Close / Save & Close when changed. */
  static async canProceed(component: any, injector: Injector): Promise<boolean> {
    if (UnsavedChangesDialog.bypass || !component || onAuthPage()) return true;
    if (Date.now() - UnsavedChangesDialog.lastSuccessAt < JUST_SAVED_MS) return true;
    if (!UnsavedChangesDialog.hasChanges(component)) {
      // Screens whose forms live in child components / tabs: read the edited form from the page itself.
      const form = UnsavedChangesDialog.isSuppressed(component) ? null : UnsavedChangesDialog.domDirtyForm();
      return form ? UnsavedChangesDialog.confirmModal(form, injector, true) : true;
    }

    const choice = await UnsavedChangesDialog.open(injector);
    switch (choice) {
      case 'close':
        return true;
      case 'save-close':
        return UnsavedChangesDialog.save(component, injector);
      case 'save':
        await UnsavedChangesDialog.save(component, injector);
        return false;
      default:
        return false;
    }
  }

  private static hasChanges(component: any): boolean {
    if (typeof component.hasUnsavedChanges === 'function') return component.hasUnsavedChanges() === true;
    if (UnsavedChangesDialog.isSuppressed(component) || !UnsavedChangesDialog.userEdited) return false;
    return UnsavedChangesDialog.dirtyControls(component).length > 0;
  }

  // View-only, request in flight, or a closed modal form: never prompt.
  private static isSuppressed(component: any): boolean {
    return (
      READ_ONLY_FLAGS.some((f) => component[f] === true) ||
      BUSY_FLAGS.some((f) => component[f] === true) ||
      MODAL_FLAGS.some((f) => component[f] === false)
    );
  }

  // First edited <form> on screen (Angular puts .ng-dirty on it), ignoring the pop-up itself.
  static domDirtyForm(): HTMLElement | null {
    if (!UnsavedChangesDialog.userEdited) return null;
    const forms = Array.from(document.querySelectorAll<HTMLElement>('form.ng-dirty'));
    return forms.find((f) => !f.closest('app-unsaved-changes-dialog')) ?? null;
  }

  // Whole forms only (FormGroup / FormArray): a lone dirty FormControl is almost always a search or filter box.
  private static dirtyControls(component: any): AbstractControl[] {
    return Object.values(component).filter(
      (v): v is AbstractControl => (v instanceof FormGroup || v instanceof FormArray) && v.dirty,
    );
  }

  /** True when any page currently on screen has unsaved changes (used for tab close / refresh). */
  static anyActiveChanges(contexts: ChildrenOutletContexts): boolean {
    return UnsavedChangesDialog.changedComponent(contexts) !== null;
  }

  /** The page currently on screen that has unsaved changes, or null. */
  static changedComponent(contexts: ChildrenOutletContexts): any {
    const visit = (ctx: ChildrenOutletContexts): any => {
      const context = ctx.getContext('primary');
      const instance = context?.outlet?.isActivated ? context.outlet.component : null;
      if (instance && UnsavedChangesDialog.hasChanges(instance)) return instance;
      return context?.children ? visit(context.children) : null;
    };
    return visit(contexts);
  }

  /** Lets the next action through unasked (a confirmed Close / Add New click, and any navigation it triggers). */
  static allowNext(): void {
    UnsavedChangesDialog.bypass = true;
    setTimeout(() => (UnsavedChangesDialog.bypass = false), BYPASS_GRACE_MS);
  }

  /** Marks the screen's forms pristine once the user chose to discard, so the next open starts clean. */
  static markDiscarded(component: any): void {
    UnsavedChangesDialog.dirtyControls(component).forEach((c) => c.markAsPristine());
    UnsavedChangesDialog.resetUserEdit();
  }

  /** Remember when a save last reported success, so the navigation a save triggers is not questioned. */
  static noteSuccess(): void {
    UnsavedChangesDialog.lastSuccessAt = Date.now();
    UnsavedChangesDialog.resetUserEdit();
  }

  /** Same choices for a modal / form with no known owner: Save presses its own Save button; true = let the close / leave go ahead. */
  static async confirmModal(root: HTMLElement, injector: Injector, strict = false): Promise<boolean> {
    const choice = await UnsavedChangesDialog.open(injector);
    if (choice === 'close') return true;
    if (choice === 'stay') return false;
    const saved = await UnsavedChangesDialog.pressSave(root, injector, strict);
    return choice === 'save-close' ? saved : false;
  }

  // Presses the nearest Save button around `root` and waits for the success / error toast. strict = page forms (no "Add").
  private static async pressSave(root: HTMLElement, injector: Injector, strict: boolean): Promise<boolean> {
    const label = strict ? /^(save|submit|update|create|apply)\b/i : /^(save|submit|update|create|add|apply)\b/i;
    let saveButton: HTMLButtonElement | undefined;
    for (let el: HTMLElement | null = root; el && el !== document.body && !saveButton; el = el.parentElement) {
      saveButton = Array.from(el.querySelectorAll<HTMLButtonElement>('button')).find(
        (b) =>
          !b.disabled &&
          !b.classList.contains('close-btn') &&
          (b.type === 'submit' || b.classList.contains('confirm-btn') || label.test((b.textContent ?? '').trim())),
      );
    }
    if (!saveButton) return false;

    root.querySelectorAll('.ng-invalid').forEach((el) => el.classList.add('ng-touched'));
    const toaster = injector.get(ToastService);
    const knownToastIds = new Set(toaster.toasts().map((t) => t.id));
    UnsavedChangesDialog.bypass = true;
    try {
      saveButton.click();
      return await UnsavedChangesDialog.saveSucceeded(toaster, knownToastIds, () => root.isConnected && !!root.querySelector('.ng-invalid'));
    } finally {
      setTimeout(() => (UnsavedChangesDialog.bypass = false), BYPASS_GRACE_MS);
    }
  }

  private static async save(component: any, injector: Injector): Promise<boolean> {
    if (typeof component.saveChanges === 'function') return (await component.saveChanges()) === true;

    // Validate changed controls first so a missing mandatory value keeps the user here with inline messages.
    const dirty = UnsavedChangesDialog.dirtyControls(component);
    dirty.forEach((c) => c.markAllAsTouched());
    if (typeof component.submit === 'boolean') component.submit = true;
    if (dirty.some((c) => c.invalid)) return false;

    const method = SAVE_METHODS.find((m) => typeof component[m] === 'function' && component[m].length === 0);
    if (!method) {
      // No zero-argument save method (e.g. onSubmitCompanyInfo): press the form's own Save button instead.
      const form = UnsavedChangesDialog.domDirtyForm();
      return form ? UnsavedChangesDialog.pressSave(form, injector, true) : false;
    }

    const toaster = injector.get(ToastService);
    const knownToastIds = new Set(toaster.toasts().map((t) => t.id));
    UnsavedChangesDialog.bypass = true;
    try {
      const result = component[method]();
      if (isObservable(result)) {
        await firstValueFrom(result, { defaultValue: undefined });
      } else if (result && typeof result.then === 'function') {
        const value = await result;
        if (value === true || value === false) return value;
      }
      return await UnsavedChangesDialog.saveSucceeded(toaster, knownToastIds);
    } finally {
      // The save usually navigates away itself once it succeeds - let that through, then re-arm.
      setTimeout(() => (UnsavedChangesDialog.bypass = false), BYPASS_GRACE_MS);
    }
  }

  // A success toast = saved, an error toast = failed; no toast in time counts as not saved.
  private static async saveSucceeded(
    toaster: ToastService,
    knownToastIds: Set<number>,
    invalidNow?: () => boolean,
  ): Promise<boolean> {
    const start = Date.now();
    const deadline = start + SAVE_WAIT_MS;
    while (Date.now() < deadline) {
      // Validation stopped the save with only inline messages (no toast): don't wait the full time.
      if (invalidNow && Date.now() - start > 1500 && invalidNow()) {
        const fresh = toaster.toasts().filter((t) => !knownToastIds.has(t.id));
        if (!fresh.some((t) => t.variant === 'success')) return false;
      }
      const fresh = toaster.toasts().filter((t) => !knownToastIds.has(t.id));
      if (fresh.some((t) => t.variant === 'error')) return false;
      if (fresh.some((t) => t.variant === 'success')) return true;
      await new Promise((r) => setTimeout(r, POLL_MS));
    }
    return false;
  }
}

/** Guards Cancel, back, browser back and menu changes via router navigation. */
export const unsavedChangesGuard: CanDeactivateFn<unknown> = (component) =>
  UnsavedChangesDialog.canProceed(component, inject(Injector));

/** Adds the guard to every page route (leaf routes with a component), including lazily loaded route files. */
function withGuard(routes: Routes): Routes {
  return routes.map((route): Route => {
    const next: Route = { ...route };
    if (next.children) next.children = withGuard(next.children);

    if (next.loadChildren) {
      const load = next.loadChildren;
      next.loadChildren = () => {
        const loaded: any = (load as () => any)();
        const wrap = (r: any) => {
          const list = Array.isArray(r) ? r : Array.isArray(r?.default) ? r.default : null;
          return list ? withGuard(list) : r;
        };
        return loaded && typeof loaded.then === 'function' ? loaded.then(wrap) : wrap(loaded);
      };
    }

    const isPage = !!(next.component || next.loadComponent) && !next.children && !next.loadChildren;
    if (isPage) {
      const existing = next.canDeactivate ?? [];
      if (!existing.includes(unsavedChangesGuard)) next.canDeactivate = [...existing, unsavedChangesGuard];
    }
    return next;
  });
}

/**
 * One line in app.config.ts providers turns the behaviour on for every page, current and future.
 * `enabled: false` registers nothing (no route guard, no click / key / unload interception), so the prompt can be
 * switched off in one place without commenting this code out.
 */
export function provideUnsavedChangesGuard(options: { enabled?: boolean } = {}) {
  if (options.enabled === false) return makeEnvironmentProviders([]);
  return makeEnvironmentProviders([
    provideAppInitializer(() => {
      const router = inject(Router);
      const contexts = inject(ChildrenOutletContexts);
      const toaster = inject(ToastService);
      const injector = inject(Injector);

      router.resetConfig(withGuard(router.config));

      // Everything registered here lives for the whole app; this releases it if the injector is ever destroyed.
      const destroyRef = inject(DestroyRef);

      // A new screen starts "unedited", so a form that was filled by code (edit / bug-fix screens) never prompts by itself.
      const navigationSub = router.events.subscribe((e) => {
        if (e instanceof NavigationEnd) UnsavedChangesDialog.resetUserEdit();
      });

      // Real typing / picking marks the screen as edited. event.isTrusted skips programmatic dispatches.
      const editable = 'input, textarea, select, [contenteditable="true"], app-dropdown, app-datepicker, app-checkbox, app-switch, app-fileupload, .cdk-overlay-pane';
      const noteEdit = (event: Event) => {
        const target = event.target as HTMLElement | null;
        if (event.isTrusted && target?.closest?.(editable) && !target.closest('app-unsaved-changes-dialog, app-search')) {
          UnsavedChangesDialog.noteUserEdit();
        }
      };
      const editEvents = ['input', 'change', 'click'] as const;
      editEvents.forEach((name) => document.addEventListener(name, noteEdit, true));
      destroyRef.onDestroy(() => {
        navigationSub.unsubscribe();
        editEvents.forEach((name) => document.removeEventListener(name, noteEdit, true));
      });
      // Note when a save last reported success, so the navigation a save triggers is not questioned.
      const seen = new Set<number>();
      effect(
        () => {
          for (const t of toaster.toasts()) {
            if (!seen.has(t.id)) {
              seen.add(t.id);
              if (t.variant === 'success') UnsavedChangesDialog.noteSuccess();
            }
          }
        },
        { injector },
      );

      // Modal X / backdrop / Cancel closes without navigation: intercept, detect .ng-dirty in the modal, then replay the click.
      let replaying = false;
      function tryInterceptAddNew(event: MouseEvent, target: HTMLElement): boolean {
        const addButton = target.closest('button');
        if (!addButton || addButton.closest('.modal-container') || !ADD_NEW.test((addButton.textContent ?? '').trim())) {
          return false;
        }
        const owner = UnsavedChangesDialog.changedComponent(contexts);
        const form = owner ? null : UnsavedChangesDialog.domDirtyForm();
        if (!owner && !form) return false;

        event.preventDefault();
        event.stopImmediatePropagation();

        const checkPromise = owner
          ? UnsavedChangesDialog.canProceed(owner, injector)
          : UnsavedChangesDialog.confirmModal(form as HTMLElement, injector, true);

        checkPromise
          .then((proceed) => {
            if (!proceed || !addButton.isConnected) return;
            if (owner) UnsavedChangesDialog.markDiscarded(owner);
            replaying = true;
            UnsavedChangesDialog.allowNext();
            try {
              addButton.click();
            } finally {
              replaying = false;
            }
          })
          .catch(() => undefined);
        return true;
      }

      function tryInterceptModalDismiss(event: MouseEvent, target: HTMLElement): void {
        const container = target.closest('.modal-container');
        let overlay: HTMLElement | null = null;
        if (!container && target.classList?.contains('modal-overlay')) {
          overlay = target;
        }
        const modal = (container ?? overlay?.querySelector('.modal-container')) as HTMLElement | null;
        if (!modal) return;

        const button = target.closest('button');
        const isClose = !!button && (button.classList.contains('close-btn') || button.classList.contains('ucd-x'));
        const isCancel = !!button && !isClose && /^(cancel|close)$/i.test((button.textContent ?? '').trim());
        if (!isClose && !isCancel && !overlay) return;

        const hasField = !!modal.querySelector('form, input, textarea, select');
        const component = hasField ? UnsavedChangesDialog.changedComponent(contexts) : null;
        const domDirty = !!modal.querySelector('form.ng-dirty, input.ng-dirty, textarea.ng-dirty, select.ng-dirty, .ng-dirty');
        if (!component && !domDirty) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        const decide = component
          ? UnsavedChangesDialog.canProceed(component, injector)
          : UnsavedChangesDialog.confirmModal(modal, injector);

        decide
          .then((proceed) => {
            if (!proceed || !target.isConnected) return;
            if (component) UnsavedChangesDialog.markDiscarded(component);
            replaying = true;
            UnsavedChangesDialog.allowNext();
            try {
              target.click();
            } finally {
              replaying = false;
            }
          })
          .catch(() => undefined);
      }
      document.addEventListener(
        'click',
        (event: MouseEvent) => {
          if (replaying || onAuthPage()) return;
          const target = event.target as HTMLElement | null;
          if (!target?.closest) return;

          if (tryInterceptAddNew(event, target)) return;
          tryInterceptModalDismiss(event, target);
        },
        true,
      );

      // Refresh keys (F5 / Ctrl+R): a page cannot restyle the browser's own reload prompt, so intercept the key and show
      // this pop-up instead; Close reloads, Stay Here keeps the form.
      let reloadConfirmed = false;
      document.addEventListener(
        'keydown',
        (event: KeyboardEvent) => {
          const isReload = event.key === 'F5' || ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'r');
          if (!isReload || onAuthPage()) return;
          if (!UnsavedChangesDialog.anyActiveChanges(contexts) && !UnsavedChangesDialog.domDirtyForm()) return;
          event.preventDefault();
          UnsavedChangesDialog.open(injector).then((choice) => {
            if (choice !== 'close') return;
            reloadConfirmed = true;
            window.location.reload();
          });
        },
        true,
      );

      // Reload button / window or tab close: a page cannot show its own pop-up there, so Chrome's own prompt is used.
      window.addEventListener('beforeunload', (event) => {
        if (reloadConfirmed || onAuthPage()) return;
        if (UnsavedChangesDialog.anyActiveChanges(contexts) || UnsavedChangesDialog.domDirtyForm()) {
          event.preventDefault();
          event.returnValue = '';
        }
      });
    }),
  ]);
}
